import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { AddEventInvitees, SendEventMessage } from "../../../schemas/event";
import type { CreateEvent, UpdateEvent } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useReflectionsForEvent(clubId: string | null, eventId: string) {
	return useQuery({
		enabled: !!clubId && !!eventId,
		queryFn: () => clubApi.reflections.listForEvent(clubId!, eventId),
		queryKey: queryKeys.clubPortal.reflections(clubId ?? "", eventId),
	});
}

export function useReflectionsSummary(clubId: string | null, eventId: string) {
	return useQuery({
		enabled: !!clubId && !!eventId,
		queryFn: () => clubApi.reflections.summaryForEvent(clubId!, eventId),
		queryKey: queryKeys.clubPortal.reflectionsSummary(clubId ?? "", eventId),
	});
}

// The unranged list. The calendar views fetch a window instead, so this query
// is parked while one of them is on screen.
export function useClubEvents(clubId: string | null, eventType?: string, enabled = true) {
	return useQuery({
		enabled: !!clubId && enabled,
		queryFn: () => clubApi.events.list(clubId!, eventType ? { event_type: eventType } : undefined),
		queryKey: queryKeys.clubPortal.events(clubId ?? "", eventType),
	});
}

// The calendar's fetch. Previous data is kept while a new month loads so
// navigating months never blanks the grid.
export function useClubEventsRange(
	clubId: string | null,
	from: Date,
	to: Date,
	eventType?: string,
	// The list view fetches unranged, so the calendar's query is parked rather
	// than fetching a window nothing draws.
	enabled = true,
) {
	return useQuery({
		enabled: !!clubId && enabled,
		placeholderData: keepPreviousData,
		queryFn: () => clubApi.events.list(clubId!, { event_type: eventType, from, to }),
		queryKey: queryKeys.clubPortal.eventsRange(clubId ?? "", from, to, eventType),
	});
}

export function useClubEventDetail(clubId: string | null, eventId: string) {
	return useQuery({
		enabled: !!clubId && !!eventId,
		queryFn: () => clubApi.events.get(clubId!, eventId),
		queryKey: queryKeys.clubPortal.eventDetail(clubId ?? "", eventId),
	});
}

export function useCreateEvent(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateEvent) => clubApi.events.create(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "events"],
			}),
	});
}

export function useUpdateEvent(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ eventId, data }: { eventId: string; data: UpdateEvent }) =>
			clubApi.events.update(clubId, eventId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "events"],
			}),
	});
}

export function useDeleteEvent(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (eventId: string) => clubApi.events.delete(clubId, eventId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "events"],
			}),
	});
}

export function useEventRSVP(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ eventId, status }: { eventId: string; status: string }) =>
			clubApi.events.rsvp(clubId, eventId, status),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "events"],
			}),
	});
}

export function useUploadEventCoverImage(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ eventId, file }: { eventId: string; file: File }) =>
			clubApi.events.uploadCoverImage(clubId, eventId, file),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "events"],
			}),
	});
}

export function useCheckInAttendee(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (eventId: string) => clubApi.events.checkIn(clubId, eventId),
		onSuccess: (_, eventId) =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.eventDetail(clubId, eventId),
			}),
	});
}

export function useEventInvitees(clubId: string | null, eventId: string) {
	return useQuery({
		enabled: !!clubId && !!eventId,
		queryFn: () => clubApi.events.invitees.list(clubId!, eventId),
		queryKey: queryKeys.clubPortal.eventInvitees(clubId ?? "", eventId),
	});
}

export function useAddEventInvitees(clubId: string, eventId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: AddEventInvitees) => clubApi.events.invitees.add(clubId, eventId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.eventDetail(clubId, eventId),
			}),
	});
}

export function useRevokeEventInvitee(clubId: string, eventId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (userId: string) => clubApi.events.invitees.revoke(clubId, eventId, userId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.eventDetail(clubId, eventId),
			}),
	});
}

// Nothing to invalidate: a push changes no state the portal shows.
export function useSendEventMessage(clubId: string, eventId: string) {
	return useMutation({
		mutationFn: (data: SendEventMessage) => clubApi.events.sendMessage(clubId, eventId, data),
	});
}
